import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  getPlan,
  getProjectDocuments,
  getProjectPositionRoster,
  getRecord,
  getRecordTypeSchema,
  listAllRecordsWithLinks,
  moveProjectDocument,
  planHas,
  renameProjectDocument,
  retypeProjectDocument,
  type LinkRow,
  type PlanApi,
  type ProjectDocument,
  type ProjectPositionRoster,
  type RecordRow,
  type RecordTypeSchema,
} from '../lib/api';
import { useRecordDetail } from '../lib/useRecordDetail';
import { openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { activeOnly } from '../lib/retired';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { fmtMoney, humanizeValue } from '../lib/fieldDisplay';
import { Strip } from '../components/Strip';
import { RecordImage } from '../components/RecordMedia';
import { AA, LINK, otherEnd, sourceKindOptionsFrom, spineFromLifecycle } from '../lib/auditionSpread';
import { disciplineOf, departmentLabel, expectsAudition } from '../lib/discipline';
import './spread.css';

// A4 — THE PROJECT: the production spine.
//
// A project GROUPS the roles read for one production. It is the legible
// anchor — scanning raw auditions is hard, and a title is what a parent
// actually remembers.
//
// ===================================================================
// SEASON IS A PROPERTY, AND THAT IS WHY THE STRIP IS A FILTER
// ===================================================================
// Decision 754e7914, in its own words: "SEASON is a property of the work,
// never a level of the hierarchy." A series is divided two ways at once —
// down by character, across by season — and neither division contains the
// other, so a tree can only ever express one of them. Nesting
// Project > Season > Role also breaks immediately for features, which have no
// seasons, leaving half the data carrying an empty rung.
//
// So: on an EPISODIC project the season strip FILTERS this screen, with an
// explicit All. On a non-episodic project NO STRIP APPEARS AT ALL. There is
// no aa_season record type in this pack and there will not be one.
//
// The proving case, from the decision: a character brought back without
// re-auditioning is ONE role with two engagements at different rates. Under a
// Season level those become two unrelated records and the character's history
// disappears. Here it is one row, and the seasons it spans are a filter over
// it.
//
// ===================================================================
// AN ANTHOLOGY IS SEPARATE LINKED PROJECTS
// ===================================================================
// Decision b13685c9. An anthology season is a different production wearing a
// shared title: new characters, new casting, new contracts, nothing carried
// across but the name. So `project_anthology_parent` is rendered as what it
// is — *part of <the other production>* — and its siblings are listed. They
// are never merged into one project with seasons, and search and the resume
// treat them as the distinct productions they are.

export interface ProjectSpineProps {
  tenantId: string;
  id: string;
  onOpenRole: (roleId: string) => void;
  onOpenProject: (projectId: string) => void;
  onBackToProjects: () => void;
  // P2 — opens the existing SourceViewer path (same door the day-of card and
  // the audition breakdown already use); optional because callers written
  // before P2 do not carry a `from` route to return to.
  onViewSource?: (sourceId: string) => void;
  // P3 — a name in the roster is a door to HouseholdPerson, the same door
  // HouseholdPeople's own rows open (App.tsx's `peopleDetail` route).
  // Optional for the same reason onViewSource is: callers written before P3
  // carry no such door yet.
  onOpenPerson?: (personId: string) => void;
}

interface RoleLine {
  role: RecordRow;
  /** Every performer who has a record against this role, with their furthest
   * state — the two-siblings case, made visible on the screen where the part
   * is the subject. */
  readers: { subjectId: string | null; furthest: string | null; count: number }[];
  /** Records on this role that name no performer. Counted, never turned into
   * a person — see the comment where this is built. */
  unattributed: number;
  seasons: number[];
  engagements: number;
}

interface Spine {
  /** See AuditionSpread's own note. "No parts recorded on this production
   * yet" is a claim about a production, not about a pending request. */
  loaded: boolean;
  roles: RoleLine[];
  anthologyParent: RecordRow | null;
  anthologySiblings: RecordRow[];
  /** The pack's own advancement line, for ranking a performer's furthest
   * state. Derived from the audition schema — no state name lives here. */
  order: string[];
  linksMissing: number;
}

const EMPTY: Spine = {
  loaded: false,
  roles: [],
  anthologyParent: null,
  anthologySiblings: [],
  order: [],
  linksMissing: 0,
};

/** A self-referential link needs a DIRECTION. `project_anthology_parent` is
 * declared on the child and points at the parent, so src is the child and dst
 * is the parent — direction-agnostic traversal here would call a project its
 * own parent's parent. */
function pointsAt(links: LinkRow[], selfId: string, linkType: string): string | null {
  for (const l of links) if (l.link_type === linkType && l.src_id === selfId) return l.dst_id;
  return null;
}
function pointedAtBy(links: LinkRow[], selfId: string, linkType: string): string[] {
  return links.filter((l) => l.link_type === linkType && l.dst_id === selfId).map((l) => l.src_id);
}

function seasonsOf(project: RecordRow): { number: number; label?: string }[] {
  const raw = project.fields.seasons;
  if (!Array.isArray(raw)) return [];
  const out: { number: number; label?: string }[] = [];
  for (const entry of raw) {
    if (entry && typeof entry === 'object' && typeof (entry as { number?: unknown }).number === 'number') {
      const e = entry as { number: number; label?: unknown };
      out.push({ number: e.number, label: typeof e.label === 'string' ? e.label : undefined });
    }
  }
  return out.sort((a, b) => a.number - b.number);
}

export function ProjectSpine({
  tenantId,
  id,
  onOpenRole,
  onOpenProject,
  onBackToProjects,
  onViewSource,
  onOpenPerson,
}: ProjectSpineProps) {
  const { record, links, error, reload } = useRecordDetail(tenantId, AA.PROJECT, id);
  const [spine, setSpine] = useState<Spine>(EMPTY);
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [season, setSeason] = useState<string>('all');

  // ===================================================================
  // THE PRODUCTION LOOKUP (agent S's sheet) — A SEAM, NOT AN IMPORT.
  // ===================================================================
  // The sheet that searches TMDb/Wikidata and attaches a poster and a
  // synopsis is a separate build. A plain `import` of a file that has not
  // landed fails the bundle and the suite; a bare dynamic import of a
  // missing module fails the bundle too, because Rollup resolves it at build
  // time.
  //
  // `import.meta.glob` is the one form that answers HONESTLY either way: an
  // empty object while the file is absent, a loader map the moment it
  // exists. So the control below renders itself when the thing it opens is
  // real, and this file needs no edit on the day S lands.
  const lookupLoader = (
    import.meta.glob('../components/ProductionLookupSheet.tsx') as Record<
      string,
      () => Promise<unknown>
    >
  )['../components/ProductionLookupSheet.tsx'];
  const [Lookup, setLookup] = useState<ProductionLookupSheet | null>(null);
  const [lookupOpen, setLookupOpen] = useState(false);
  const [lookupSay, setLookupSay] = useState<string | null>(null);
  // Bumped when the sheet reports it applied something, so the poster is
  // re-fetched rather than served from the element that already failed to
  // find one.
  const [artNonce, setArtNonce] = useState(0);

  async function openLookup() {
    setLookupSay(null);
    if (!lookupLoader) return;
    if (!Lookup) {
      try {
        const mod = (await lookupLoader()) as { ProductionLookupSheet?: ProductionLookupSheet };
        if (!mod.ProductionLookupSheet) throw new Error('the sheet exports no ProductionLookupSheet');
        // A component is a function, and a state setter given a function
        // CALLS it — so it goes in through the updater form.
        setLookup(() => mod.ProductionLookupSheet!);
      } catch (err) {
        // Loud, named, and in the failure's own words. Never a silent
        // control that does nothing when tapped.
        setLookupSay(
          `The production lookup could not be opened — ${err instanceof Error ? err.message : 'it did not load'}.`
        );
        return;
      }
    }
    setLookupOpen(true);
  }
  const recordsVersion = useRecordsVersion();

  // ===================================================================
  // P2 — THE PROJECT BINDER (docs.binder, Pro)
  // ===================================================================
  // The LIST is ungated on purpose: filing put every one of these here on
  // every tier, and Pro adds MANAGEMENT over the same list, never a second
  // copy of it. `plan` decides only whether the edit affordance appears.
  const [plan, setPlan] = useState<PlanApi | null>(null);
  const [documents, setDocuments] = useState<ProjectDocument[]>([]);
  const [docsSay, setDocsSay] = useState<string | null>(null);
  const [sourceSchema, setSourceSchema] = useState<RecordTypeSchema | null>(null);
  const [moveTargets, setMoveTargets] = useState<RecordRow[]>([]);
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [editFilename, setEditFilename] = useState('');
  const [editKind, setEditKind] = useState('');
  const [editProjectId, setEditProjectId] = useState('');
  const [docBusy, setDocBusy] = useState(false);
  const [docRefusal, setDocRefusal] = useState<string | null>(null);
  const binderOn = planHas(plan, 'docs.binder');

  // ===================================================================
  // P3 — THE POSITION ROSTER (people.position_roster, Pro)
  // ===================================================================
  // ⚠ THE ROUTE IS GATED WHOLE (unlike the binder just above): there is no
  // free half of "the roster" to fetch and half-render. So below Pro this
  // never calls the server at all — the affordance itself is gated, not
  // merely the edit control — and the panel shows one honest, unconditional
  // teaser line instead (craft.layer's own AuditionSpread.tsx precedent).
  const [roster, setRoster] = useState<ProjectPositionRoster | null>(null);
  const [rosterSay, setRosterSay] = useState<string | null>(null);
  const rosterOn = planHas(plan, 'people.position_roster');

  useEffect(() => {
    let cancelled = false;
    // A plan that fails to load leaves `plan` null, and `planHas` answers
    // false for null — the safe direction: nothing is offered.
    void getPlan(tenantId)
      .then((p) => {
        if (!cancelled) setPlan(p);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    if (!rosterOn) {
      setRoster(null);
      setRosterSay(null);
      return;
    }
    let cancelled = false;
    void getProjectPositionRoster(tenantId, id)
      .then((r) => {
        if (!cancelled) {
          setRoster(r);
          setRosterSay(null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        // 403/404 reads as "cannot see this production's roster at all" — a
        // blank, the same posture reloadDocuments takes for the binder.
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          setRoster(null);
          setRosterSay(null);
        } else {
          setRosterSay('The people on this production could not be loaded just now.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, id, rosterOn, recordsVersion]);

  async function reloadDocuments() {
    try {
      const rows = await getProjectDocuments(tenantId, id);
      setDocuments(rows);
      setDocsSay(null);
    } catch (err) {
      // 403/404 reads as "cannot see this production's papers at all" — a
      // blank, the same line the rest of this screen draws for a read the
      // caller may not make. Anything else is said out loud.
      if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
        setDocuments([]);
        setDocsSay(null);
      } else {
        setDocsSay('The documents filed to this production could not be loaded just now.');
      }
    }
  }

  useEffect(() => {
    void reloadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, id, recordsVersion]);

  // The re-type control's vocabulary — read off aa_source's own schema
  // (sourceKindOptionsFrom), never copied. Loaded once binder editing is
  // reachable at all; a household on Working never pays for this fetch.
  useEffect(() => {
    if (!binderOn) return;
    let cancelled = false;
    void getRecordTypeSchema(tenantId, AA.SOURCE)
      .then((s) => {
        if (!cancelled) setSourceSchema(s);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tenantId, binderOn]);

  function beginDocEdit(doc: ProjectDocument) {
    setEditingDocId(doc.id);
    setEditFilename(doc.filename ?? '');
    setEditKind(doc.kind ?? '');
    setEditProjectId(id);
    setDocRefusal(null);
    if (moveTargets.length === 0) {
      void listAllRecordsWithLinks(tenantId, AA.PROJECT, {})
        .then((res) => setMoveTargets(activeOnly(res.records)))
        .catch(() => {
          // The move control degrades to "just this production" rather than
          // erroring the whole edit affordance — rename and re-type still
          // work. SEEDED with the production we are standing on: a select
          // with zero options is a control that lies about the world, and
          // the current project is the one target a failed read still knows.
          setMoveTargets(record ? [record] : []);
        });
    }
  }

  function cancelDocEdit() {
    setEditingDocId(null);
    setDocRefusal(null);
  }

  async function saveDocEdit(doc: ProjectDocument) {
    setDocBusy(true);
    setDocRefusal(null);
    try {
      const trimmedName = editFilename.trim();
      if (trimmedName && trimmedName !== (doc.filename ?? '')) {
        await renameProjectDocument(tenantId, doc.id, trimmedName);
      }
      if (editKind && editKind !== (doc.kind ?? '')) {
        await retypeProjectDocument(tenantId, doc.id, editKind);
      }
      if (editProjectId && editProjectId !== id) {
        await moveProjectDocument(tenantId, doc.id, editProjectId);
      }
      setEditingDocId(null);
      await reloadDocuments();
    } catch (err) {
      setDocRefusal(err instanceof ApiError ? err.message : 'that change was refused');
    } finally {
      setDocBusy(false);
    }
  }

  function formatReceived(iso: string | null): string | null {
    if (!iso) return null;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  useEffect(() => {
    if (!record) return;
    let cancelled = false;
    setSpine((prev) => ({ ...prev, loaded: false }));
    async function load() {
      try {
        const [auditionSchema, allRoles, auds, engs] = await Promise.all([
          getRecordTypeSchema(tenantId, AA.AUDITION).catch(() => null),
          listAllRecordsWithLinks(tenantId, AA.ROLE, { linkTypes: [LINK.ROLE_PROJECT] }),
          listAllRecordsWithLinks(tenantId, AA.AUDITION, {
            linkTypes: [LINK.AUDITION_ROLE, LINK.AUDITION_SUBJECT],
          }),
          listAllRecordsWithLinks(tenantId, AA.ENGAGEMENT, {
            linkTypes: [LINK.ENGAGEMENT_ROLE, LINK.ENGAGEMENT_SUBJECT],
          }),
        ]);
        if (cancelled) return;

        const order = spineFromLifecycle(auditionSchema?.lifecycle ?? undefined).spine;
        const mineRoles = activeOnly(allRoles.records).filter(
          (r) => otherEnd(allRoles.linksByRecordId[r.id] ?? [], r.id, LINK.ROLE_PROJECT) === id
        );

        const roles: RoleLine[] = mineRoles.map((role) => {
          const roleAuds = activeOnly(auds.records).filter(
            (a) => otherEnd(auds.linksByRecordId[a.id] ?? [], a.id, LINK.AUDITION_ROLE) === role.id
          );
          const roleEngs = activeOnly(engs.records).filter(
            (e) => otherEnd(engs.linksByRecordId[e.id] ?? [], e.id, LINK.ENGAGEMENT_ROLE) === role.id
          );
          // ⚑ AN UNATTRIBUTED RECORD IS NOT A PERSON.
          //
          // The key here is a subject id, and a record with no performer on
          // it keyed to `''` — which then counted as somebody. A part with
          // one read that named nobody and one booking that named Rae showed
          // "2 performers", which is not a rounding error: it is the screen
          // inventing a second actor.
          //
          // So the empty key is tracked SEPARATELY. What it means is "some of
          // this is not attributed yet", which is a true and useful thing to
          // say, and a very different one from "two people".
          const byReader = new Map<string, { furthest: string | null; count: number }>();
          let unattributed = 0;
          for (const a of roleAuds) {
            const sid = otherEnd(auds.linksByRecordId[a.id] ?? [], a.id, LINK.AUDITION_SUBJECT) ?? '';
            if (!sid) {
              unattributed += 1;
              continue;
            }
            const cur = byReader.get(sid) ?? { furthest: null, count: 0 };
            const rankNew = order.indexOf(a.lifecycle_state ?? '');
            const rankOld = cur.furthest ? order.indexOf(cur.furthest) : -1;
            byReader.set(sid, {
              // An off-spine state (passed, archived) ranks -1 and never wins
              // against a live one: "furthest" means how far it GOT, and a
              // pass is where it stopped, not how far it went.
              furthest: rankNew > rankOld ? a.lifecycle_state : cur.furthest,
              count: cur.count + 1,
            });
          }
          // ⚑ A BOOKING MAKES SOMEBODY ATTACHED, TOO.
          //
          // This map used to be built from AUDITIONS alone, so a role's "who"
          // was really "who read". A crew job has no reads by definition, so
          // every crew job on this screen said "Nobody is attached to this
          // job yet" while two people were booked on it — which the A11
          // capture photographed before anybody noticed.
          //
          // And it was never only a crew problem: the pack says plainly that
          // `engagement_audition` is optional by design, for the offer that
          // arrives without a read. Any actor booked straight off a tape the
          // agent sent had the same blank.
          for (const e of roleEngs) {
            const sid = otherEnd(engs.linksByRecordId[e.id] ?? [], e.id, LINK.ENGAGEMENT_SUBJECT) ?? '';
            if (!sid) {
              unattributed += 1;
              continue;
            }
            const cur = byReader.get(sid);
            // `furthest` stays an AUDITION-spine word — an engagement's
            // states are a different ladder and mixing them would put
            // "shooting" where "callback" belongs. Somebody with only a
            // booking has no furthest, and the line says "booked" instead.
            if (!cur) byReader.set(sid, { furthest: null, count: 0 });
          }

          const seasons = [
            ...new Set(
              [...roleAuds, ...roleEngs]
                .map((r) => r.fields.season)
                .filter((s): s is number => typeof s === 'number' && Number.isFinite(s))
            ),
          ].sort((a, b) => a - b);
          return {
            role,
            readers: [...byReader].map(([sid, v]) => ({
              subjectId: sid || null,
              furthest: v.furthest,
              count: v.count,
            })),
            seasons,
            unattributed,
            engagements: roleEngs.length,
          };
        });

        // Anthology: the parent this project is part of, and the siblings
        // pointing back at it. Direction matters — see pointsAt/pointedAtBy.
        const parentId = pointsAt(links, id, LINK.ANTHOLOGY_PARENT);
        const siblingIds = pointedAtBy(links, id, LINK.ANTHOLOGY_PARENT);
        const [anthologyParent, ...siblings] = await Promise.all([
          parentId ? readOrNull(tenantId, parentId) : Promise.resolve(null),
          ...siblingIds.map((sid) => readOrNull(tenantId, sid)),
        ]);
        if (cancelled) return;

        setSpine({
          loaded: true,
          roles,
          anthologyParent,
          anthologySiblings: siblings.filter((r): r is RecordRow => r !== null),
          order,
          linksMissing: auds.unreadable.length + engs.unreadable.length,
        });
        setFailure(null);
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, id, record?.id, recordsVersion, links.length]);

  if (error) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">Project</div>
        <div class="sp__say">
          {error.headline}
          <br />
          <span class="sp__say-soft">{error.detail}</span>
        </div>
        {error.retryable ? (
          <button type="button" class="sp__retry" onClick={reload}>
            Read it again
          </button>
        ) : null}
      </div>
    );
  }

  if (!record) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">Project</div>
        <div class="sp__say">One moment…</div>
      </div>
    );
  }

  const episodic = record.fields.episodic === true;
  const seasons = seasonsOf(record);
  // THE STRIP EXISTS ONLY WHERE THE WORK HAS SEASONS. A feature shows none —
  // not an empty strip, not a disabled one. (754e7914: a tree can express only
  // one of the two divisions, so season is a filter or it is nothing.)
  const showStrip = episodic && seasons.length > 0;
  const stripItems = showStrip
    ? [
        { id: 'all', label: 'All' },
        ...seasons.map((s) => ({ id: String(s.number), label: s.label ?? `Season ${s.number}` })),
      ]
    : [];
  const shown =
    season === 'all'
      ? spine.roles
      : spine.roles.filter((r) => r.seasons.includes(Number(season)));

  const kind = record.fields.kind;
  // A9 — the two halves of a production, counted separately, because the
  // aside's headline was "2 parts" on a production holding one part and one
  // crew job. A count is a claim, and that one was false.
  const castCount = spine.roles.filter((r) => disciplineOf(r.role) === 'performance').length;
  const crewCount = spine.roles.length - castCount;
  const totalReads = spine.roles.reduce((n, r) => n + r.readers.reduce((m, x) => m + x.count, 0), 0);
  const totalEngagements = spine.roles.reduce((n, r) => n + r.engagements, 0);

  // ⚑ THE PRODUCTION'S STANDING IS ONE LINE, IN THE SUBJECT BLOCK.
  //
  // It used to be the aside's headline: "1 part" set at the size of a title,
  // with "1 read · 1 booked" under it — half a phone screen restating in
  // large type exactly what the parts ledger says two inches lower. The
  // biggest thing on the spread was a summary of the second-biggest.
  //
  // The words are unchanged (the cast/crew split is A9's, and a read count
  // still belongs to the cast half — on a crew-only production "0 reads"
  // measures something that does not exist). Only their rank moved.
  const partsWord =
    crewCount === 0
      ? `${castCount} ${castCount === 1 ? 'part' : 'parts'}`
      : castCount === 0
        ? `${crewCount} crew ${crewCount === 1 ? 'job' : 'jobs'}`
        : `${castCount} cast · ${crewCount} crew`;
  const standing = [
    partsWord,
    castCount > 0 ? `${totalReads} ${totalReads === 1 ? 'read' : 'reads'}` : null,
    totalEngagements > 0 ? `${totalEngagements} booked` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  // A9's `sidesHere` precedent, one screen over: the aside column exists only
  // when something belongs in it. An anthology parent's other seasons are the
  // one thing a production has to say beside itself, and most productions are
  // not anthologies — a permanently empty half-width gutter is a hole
  // announcing what is missing (the note on .sp__grid--nosides says so).
  const hasKin = spine.anthologySiblings.length > 0;

  // ⚑ READ, NEVER ASSUMED. `poster_ref` is declared on aa_project; `synopsis`
  // and `attribution` are what the production lookup writes, and a field the
  // record does not carry renders NOTHING here rather than an empty slot.
  const hasArt = typeof record.fields.poster_ref === 'string' && record.fields.poster_ref !== '';
  const synopsis =
    typeof record.fields.synopsis === 'string' && record.fields.synopsis.trim()
      ? record.fields.synopsis.trim()
      : null;
  // ⚑ ATTRIBUTION IS A LIST, BECAUSE TWO DIFFERENT PEOPLE ARE OWED IT.
  //
  // The FACTS come from Wikidata (CC0) and are attributed on the project; the
  // IMAGE comes from Wikimedia Commons under whatever licence its uploader
  // chose, and that credit belongs to the image, not to the production. They
  // are two separate obligations and either can be present without the other,
  // so they render as two lines and neither is invented when it is absent.
  //
  // ⚠ THE IMAGE CREDIT'S HOME IS NOT SETTLED. The lookup build (agent S) puts
  // it on the media row; nothing this screen reads today carries a media row,
  // so it is read here from `image_attribution` on the project. One line
  // changes when S says where it lives — the slot, its place, its type and
  // its test are already here.
  const attributions = [
    typeof record.fields.image_attribution === 'string' && record.fields.image_attribution.trim()
      ? record.fields.image_attribution.trim()
      : null,
    typeof record.fields.attribution === 'string' && record.fields.attribution.trim()
      ? record.fields.attribution.trim()
      : null,
  ].filter((line): line is string => line !== null);

  return (
    <div class="sp">
      <div class="sp__crumb">
        <button type="button" onClick={onBackToProjects}>
          Projects
        </button>
        <span class="sp__crumb-sep">›</span>
        <span class="sp__crumb-cur">{record.display_name}</span>
      </div>

      {showStrip ? (
        <Strip items={stripItems} activeId={season} onSelect={(s) => setSeason(s)} />
      ) : null}

      <div class={`sp__grid${hasKin ? '' : ' sp__grid--nosides'}`}>
        <div class={`sp__hero${hasArt ? ' sp__hero--art' : ''}`}>
          {/* THE SAME POSTER THE TILE SHOWS. The list and the spine agree
              about what a production looks like, or the list is decoration.
              `poster_ref` is a FILE FIELD on aa_project, so the bytes come
              through the pack's own serving door — RecordImage, exactly as
              the tile does it. */}
          {hasArt ? (
            <div
              class={`sp__hero-art${record.fields.poster_role === 'logo' ? ' sp__hero-art--logo' : ''}`}
              data-testid="project-art"
            >
              <RecordImage
                key={`${id}:${artNonce}`}
                tenantId={tenantId}
                recordId={id}
                fieldName="poster_ref"
                alt={`${record.display_name} — poster`}
              />
            </div>
          ) : null}
          <div class={hasArt ? 'sp__hero-body' : ''}>
          <div class="sp__hero-top">
            <div>
              <div class="sp__label">
                {typeof kind === 'string' && kind ? <span class="sp__chip">{humanizeValue(kind)}</span> : null}
                {typeof record.fields.year === 'number' ? <span>{record.fields.year}</span> : null}
                {typeof record.fields.company === 'string' && record.fields.company ? (
                  <span>{record.fields.company}</span>
                ) : null}
                {/* The genre lives HERE, not on the tile — the tile's two lines
                    are spent on kind, year and network. Wikidata answers a
                    list; the first is the one anybody says out loud. */}
                {firstGenre(record.fields.genre) ? (
                  <span>{firstGenre(record.fields.genre)}</span>
                ) : null}
              </div>
              <h3 class="sp__title">{record.display_name}</h3>
              <p class="sp__sub">{standing}</p>
              {/* AN ANTHOLOGY SEASON IS A DIFFERENT PRODUCTION WEARING A
                  SHARED TITLE. It is named as a link, never merged in. */}
              {spine.anthologyParent ? (
                <p class="sp__sub">
                  Part of{' '}
                  <button
                    type="button"
                    class="sp__edit"
                    style={{ padding: '1px 9px' }}
                    onClick={() => onOpenProject(spine.anthologyParent!.id)}
                  >
                    {spine.anthologyParent.display_name}
                  </button>
                </p>
              ) : null}
              {/* WHAT SOMEBODY ELSE KNOWS ABOUT THIS PRODUCTION.
                  One line, and only when a lookup has attached one — a slot
                  reading "no synopsis" would be a report of a blank. The
                  attribution rides with it, small, once, because a line
                  taken from somebody else's database says whose it is. */}
              {synopsis ? (
                <p class="sp__synopsis" data-testid="project-synopsis">
                  {synopsis}
                </p>
              ) : null}
              {attributions.length > 0 ? (
                <p class="sp__attrib" data-testid="project-attribution">
                  {attributions.map((line, i) => (
                    <span key={line} class="sp__attrib-line">
                      {i > 0 ? <br /> : null}
                      {line}
                    </span>
                  ))}
                </p>
              ) : null}
              {typeof record.fields.notes === 'string' && record.fields.notes.trim() ? (
                <p class="sp__note">{record.fields.notes}</p>
              ) : null}
            </div>
            <div class="sp__hero-tools">
              <button
                type="button"
                class="sp__edit"
                onClick={() => openRecordEditor({ mode: 'edit', typeKey: AA.PROJECT, id })}
              >
                Edit
              </button>
              {/* THE DOOR THAT GOES AND FINDS THE PRODUCTION — rendered only
                  when the sheet that answers it is actually in the bundle
                  (see lookupLoader). A control that opens nothing is worse
                  than no control. */}
              {lookupLoader ? (
                <button
                  type="button"
                  class="sp__edit"
                  data-testid="project-lookup-open"
                  onClick={() => void openLookup()}
                >
                  Find this production
                </button>
              ) : null}
            </div>
          </div>
          </div>
        </div>

        {!hasKin ? null : (
        <div class="sp__aside">
          <div class="sp__sec">
            <span>Other productions under this title</span>
          </div>
          <div class="sp__trail">
            {spine.anthologySiblings.map((s) => (
              <button type="button" key={s.id} class="sp__trail-row" onClick={() => onOpenProject(s.id)}>
                <span>{s.display_name}</span>
                <span class="sp__trail-when">
                  {typeof s.fields.year === 'number' ? s.fields.year : ''}
                </span>
              </button>
            ))}
          </div>
        </div>
        )}

        {/* ---- ROLES ARE THE BODY ----
             A9: SPLIT BY DISCIPLINE WHEN THE PRODUCTION HOLDS BOTH KINDS.
             One person is very often cast on one film and crew on the next —
             "that's actually the most common entry into crew" (Huan) — and a
             production can hold both from the same household. Listed together
             under "Parts", a gaffer reads as a character nobody cast.

             The split appears ONLY when both kinds are present. A straight
             acting production keeps exactly the screen it had, with the word
             it had; a crew-only one says Crew and never says Parts. A heading
             that names a division nobody has is furniture. */}
        <div class="sp__body">
          {!spine.loaded ? null : shown.length === 0 ? (
            <>
              <div class="sp__sec">
                <span>Parts</span>
                {season !== 'all' ? <span>season {season}</span> : null}
              </div>
              <div class="sp__empty">
                {spine.roles.length === 0
                  ? 'No parts recorded on this production yet.'
                  : 'No parts in this season.'}
              </div>
            </>
          ) : (
            groupByDiscipline(shown).map((group) => (
            <div key={group.key}>
              <div class="sp__sec">
                <span>{group.label}</span>
                {season !== 'all' ? <span>season {season}</span> : null}
              </div>
              {group.lines.map((line) => (
              <button
                type="button"
                key={line.role.id}
                class="sp__row sp__row--lead"
                onClick={() => onOpenRole(line.role.id)}
              >
                <span class="sp__row-top">
                  <span class="sp__row-name">{line.role.display_name}</span>
                  <span class="sp__row-when">
                    {/* A crew job's department is what a reader wants in
                        this slot; size is a casting word. */}
                    {departmentLabel(line.role) ??
                      (typeof line.role.fields.size === 'string' && line.role.fields.size
                        ? humanizeValue(line.role.fields.size)
                        : '')}
                  </span>
                </span>
                <span class="sp__row-sub">
                  {/* WHO READ FOR IT — the two-siblings case, visible on the
                      screen where the part is the subject. A role with one
                      reader still names them: here the performer is the
                      information, not a heading over a group. */}
                  {line.readers.length === 0 ? (
                    line.unattributed > 0 ? (
                      // Something is on this role and nobody is named on it.
                      // Saying "nobody has read for this part" would be
                      // flatly false, and inventing a performer worse.
                      `${line.unattributed} ${line.unattributed === 1 ? 'record' : 'records'}, not attributed to anyone yet`
                    ) : expectsAudition(line.role) ? (
                      'Nobody has read for this part yet.'
                    ) : (
                      'Nobody is attached to this job yet.'
                    )
                  ) : (
                    <>
                      {line.readers.length}{' '}
                      {expectsAudition(line.role)
                        ? line.readers.length === 1
                          ? 'performer'
                          : 'performers'
                        : line.readers.length === 1
                          ? 'person'
                          : 'people'}
                      {line.readers
                        .map((r) => (r.furthest ? humanizeValue(r.furthest) : null))
                        .filter(Boolean)
                        .join(' · ')
                        ? ` — ${line.readers
                            .map((r) => (r.furthest ? humanizeValue(r.furthest) : null))
                            .filter(Boolean)
                            .join(' · ')}`
                        : ''}
                      {/* "Booked · 1 booked" says one thing twice: when every reader who
                          got that far IS the booking, the stage word already said it. */}
                      {line.engagements > 0 &&
                      line.engagements !== line.readers.filter((r) => r.furthest === 'booked').length
                        ? ` · ${line.engagements} booked`
                        : ''}
                    </>
                  )}
                  {typeof line.role.fields.quoted_rate === 'number'
                    ? ` · ${fmtMoney(line.role.fields.quoted_rate)}`
                    : ''}
                </span>
              </button>
              ))}
            </div>
            ))
          )}

          {/* THE DOOR THAT MAKES ONE, AT THE FOOT OF THE LEDGER IT ADDS TO.
              It used to sit in the aside beside a count, two regions away
              from the list it changes. */}
          <div class="sp__acts">
            <button
              type="button"
              class="sp__act"
              onClick={() =>
                openRecordEditor({
                  mode: 'create',
                  typeKey: AA.ROLE,
                  prefillRels: { [LINK.ROLE_PROJECT]: [id] },
                })
              }
            >
              Add a role
            </button>
          </div>

        {/* ===================================================================
            SUPPORT, RANKED — parts, then documents, then people, INSIDE the
            spread rather than as three slabs stacked under it.
            ===================================================================
            Documents and People used to be their own `.sp__body` blocks after
            the grid closed: three separate objects with three sets of rules,
            each announcing itself as loudly as the production. They are
            support for one subject, so they live in the subject's object, in
            the order a household ranks them. */}
        <div class="sp__sec">
          <span>Documents</span>
          {documents.length > 0 ? <span>{documents.length}</span> : null}
        </div>

        {docsSay ? (
          <p class="sp__say-soft" data-testid="binder-failure">
            {docsSay}
          </p>
        ) : documents.length === 0 ? (
          // A STATEMENT OF THE SYSTEM'S OWN BEHAVIOUR, not a report of a
          // blank. "No documents filed yet" is true and tells a household
          // nothing about how one would ever get here.
          <div class="sp__empty">
            A call sheet or a contract forwarded to your address is read for you, and what you
            approve lands here, under this production.
          </div>
        ) : (
          documents.map((doc) => (
            <div class="sp__row" key={doc.id} data-testid="binder-doc" style={{ cursor: 'default' }}>
              <span class="sp__row-top">
                <button
                  type="button"
                  class="sp__row-name"
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
                  onClick={() => onViewSource?.(doc.id)}
                >
                  {doc.filename ?? humanizeValue(doc.kind ?? 'other')}
                </button>
                <span class="sp__row-when">{formatReceived(doc.receivedAt) ?? ''}</span>
              </span>
              <span class="sp__row-sub">
                {doc.kind ? humanizeValue(doc.kind) : 'Unclassified'}
              </span>

              {binderOn && editingDocId === doc.id ? (
                <div class="sp__acts" style={{ flexWrap: 'wrap', gap: '8px' }}>
                  <input
                    type="text"
                    class="sp__craft-self"
                    style={{ minHeight: 'auto', padding: '6px 10px', width: 'auto', flex: '1 1 140px' }}
                    data-testid="binder-rename-input"
                    value={editFilename}
                    onInput={(e) => setEditFilename((e.target as HTMLInputElement).value)}
                    placeholder="File name"
                  />
                  <select
                    class="sp__craft-self"
                    style={{ minHeight: 'auto', padding: '6px 10px', width: 'auto', flex: '1 1 140px' }}
                    data-testid="binder-retype-select"
                    value={editKind}
                    onChange={(e) => setEditKind((e.target as HTMLSelectElement).value)}
                  >
                    {sourceKindOptionsFrom(sourceSchema).map((opt) => (
                      <option key={opt} value={opt}>
                        {humanizeValue(opt)}
                      </option>
                    ))}
                  </select>
                  <select
                    class="sp__craft-self"
                    style={{ minHeight: 'auto', padding: '6px 10px', width: 'auto', flex: '1 1 140px' }}
                    data-testid="binder-move-select"
                    value={editProjectId}
                    onChange={(e) => setEditProjectId((e.target as HTMLSelectElement).value)}
                  >
                    {/* Never a zero-option select: until (or unless) the
                        wide read answers, the production we are standing on
                        is the one true option. */}
                    {(moveTargets.length > 0 ? moveTargets : [record]).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.display_name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    class="sp__act"
                    disabled={docBusy}
                    onClick={() => void saveDocEdit(doc)}
                  >
                    {docBusy ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" class="sp__edit" disabled={docBusy} onClick={cancelDocEdit}>
                    Cancel
                  </button>
                </div>
              ) : binderOn ? (
                <div class="sp__acts">
                  <button type="button" class="sp__edit" onClick={() => beginDocEdit(doc)}>
                    Edit
                  </button>
                </div>
              ) : null}

              {binderOn && editingDocId === doc.id && docRefusal ? (
                <p class="sp__craft-say" data-testid="binder-refusal">
                  {docRefusal}
                </p>
              ) : null}
            </div>
          ))
        )}

        {/* THE TEASER — one honest line, never a degraded copy of the
            feature and never a control that always errors: below Pro the
            documents stay exactly where filing put them, read-only. */}
        {!binderOn && documents.length > 0 ? (
          <p class="sp__craft-say" data-testid="binder-teaser">
            On Pro, the binder manages these — rename one, correct a misfiled kind, or move it to
            another production.
          </p>
        ) : null}

        <div class="sp__sec">
          <span>People</span>
          {rosterOn && roster ? <span>{roster.positions.length}</span> : null}
        </div>

        {rosterSay ? (
          <p class="sp__say-soft" data-testid="roster-failure">
            {rosterSay}
          </p>
        ) : !rosterOn ? (
          // THE TEASER — ONE LINE, quiet, at the foot of the section.
          //
          // It shipped as a two-sentence paragraph in the People section's
          // prime space, which is the shape Framework §5 refuses: an
          // advertisement standing where a household's own record should be.
          // Unconditional, because the read is gated whole (craft.layer's
          // precedent in AuditionSpread.tsx) — this screen has no way to know
          // whether there is a roster to grow into without the feature that
          // answers it. Phrased as what the product does, not as a pitch.
          <p class="sp__craft-say" data-testid="roster-teaser">
            On Pro, the crew printed on a call sheet appear here by position.
          </p>
        ) : !roster || roster.positions.length === 0 ? (
          <div class="sp__empty">No crew positions filed to this production yet.</div>
        ) : (
          roster.positions.map((group) => (
            <div key={group.position} data-testid="roster-group">
              <div class="sp__row-sub" style={{ margin: '6px 2px 2px' }}>
                {group.position}
              </div>
              {group.people.map((p) => (
                <button
                  type="button"
                  key={`${group.position}:${p.personId ?? 'unattributed'}`}
                  class="sp__row"
                  data-testid="roster-person"
                  disabled={!p.personId}
                  onClick={() => p.personId && onOpenPerson?.(p.personId)}
                >
                  <span class="sp__row-top">
                    <span class="sp__row-name">
                      {p.personName ?? 'Not attributed to anyone yet'}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          ))
        )}
        </div>

        <div class="sp__rail">
          <span class={`sp__pill${spine.roles.length === 0 ? ' sp__pill--zero' : ''}`}>
            {spine.roles.some((l) => disciplineOf(l.role) === 'crew') ? 'Roles' : 'Parts'}{' '}
            <span class="sp__pill-n">{spine.roles.length}</span>
          </span>
          <span class={`sp__pill${totalReads === 0 ? ' sp__pill--zero' : ''}`}>
            Auditions <span class="sp__pill-n">{totalReads}</span>
          </span>
          <span class={`sp__pill${totalEngagements === 0 ? ' sp__pill--zero' : ''}`}>
            Engagements <span class="sp__pill-n">{totalEngagements}</span>
          </span>
          <span class={`sp__pill${spine.anthologySiblings.length === 0 ? ' sp__pill--zero' : ''}`}>
            Linked productions <span class="sp__pill-n">{spine.anthologySiblings.length}</span>
          </span>
        </div>
      </div>

      {spine.linksMissing > 0 ? (
        <p class="sp__say-soft" style={{ margin: '12px 2px' }}>
          {spine.linksMissing} record{spine.linksMissing === 1 ? '' : 's'} could not be placed on a
          role — their links were not answered for.
        </p>
      ) : null}

      {lookupSay ? (
        <p class="sp__say-soft" style={{ margin: '12px 2px' }} data-testid="project-lookup-failure">
          {lookupSay}
        </p>
      ) : null}

      {Lookup && lookupOpen ? (
        <Lookup
          tenantId={tenantId}
          projectId={id}
          title={record.display_name}
          kind={typeof kind === 'string' ? kind : null}
          year={typeof record.fields.year === 'number' ? record.fields.year : null}
          onClose={() => setLookupOpen(false)}
          onApplied={() => {
            setLookupOpen(false);
            // The record carries the new poster ref and synopsis, and the
            // art element is keyed on the nonce so it fetches again.
            reload();
            setArtNonce((n) => n + 1);
          }}
        />
      ) : null}

      {failure ? (
        <p class="sp__say-soft" style={{ margin: '12px 2px' }}>
          {failure.headline} — {failure.detail}
        </p>
      ) : null}
    </div>
  );
}

/** THE CONTRACT THIS SCREEN EXPECTS OF THE LOOKUP SHEET (agent S's build).
 *
 * Declared here rather than imported, for the same reason the module is
 * globbed rather than imported: this file must typecheck and this suite must
 * run whether or not the sheet exists yet. When it lands, its own props are
 * the authority; this is what the spine hands it and what it promises back —
 * `onApplied` fires AFTER the project record carries the poster and the
 * synopsis, because that is the moment this screen has something new to read.
 */
type ProductionLookupSheet = (props: {
  tenantId: string;
  projectId: string;
  title: string;
  kind: string | null;
  year: number | null;
  onClose: () => void;
  onApplied: () => void;
}) => preact.ComponentChild;

/** A9 — split the production's roles into cast and crew, but ONLY when it
 * actually holds both.
 *
 * A production with acting roles alone keeps the screen it had and the word it
 * had; one with crew alone says Crew and never says Parts. A heading that
 * names a division nobody has is furniture, and two headings over one list is
 * the same furniture twice.
 *
 * Order is cast first when both exist — not a judgement about which matters,
 * but the order a call sheet and a resume both already use, and consistency
 * with things people already read costs nothing. */
function groupByDiscipline(
  lines: RoleLine[]
): { key: string; label: string; lines: RoleLine[] }[] {
  const cast = lines.filter((l) => disciplineOf(l.role) === 'performance');
  const crew = lines.filter((l) => disciplineOf(l.role) === 'crew');
  if (cast.length === 0) return [{ key: 'crew', label: 'Crew', lines: crew }];
  if (crew.length === 0) return [{ key: 'cast', label: 'Parts', lines: cast }];
  return [
    { key: 'cast', label: 'Cast', lines: cast },
    { key: 'crew', label: 'Crew', lines: crew },
  ];
}

async function readOrNull(tenantId: string, id: string): Promise<RecordRow | null> {
  try {
    return await getRecord(tenantId, id);
  } catch (err) {
    if (err instanceof ApiError && (err.status === 403 || err.status === 404)) return null;
    throw err;
  }
}

/** The genre a person would say out loud — the first of whatever list the
 * lookup wrote, or nothing. */
function firstGenre(genre: unknown): string | null {
  const list = Array.isArray(genre) ? genre : typeof genre === 'string' ? [genre] : [];
  const g = list.find((x): x is string => typeof x === 'string' && x.trim() !== '');
  return g ?? null;
}
